import axios from 'axios';
import { API_ENDPOINT } from '../constants';
import { CreateOAuthUrlPayload, OAuthUrl } from '../types';
import { LoadingStates } from '../../../constants';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  addNotification,
  createOAuthUrl,
  fetchOAuthUrls,
  setCreateOAuthUrlError,
  setCreateOAuthUrlSuccess,
  setOAuthUrls,
  setOAuthUrlsLoading,
} from '../slice';
import { auth } from 'utils/firebase';
import { call, put, takeLatest } from 'redux-saga/effects';

function handleFetchOAuthUrls() {
  return auth.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    // Always fetch all data - no filters, sorting, or pagination parameters
    return authorizedAxiosInstance.get(API_ENDPOINT);
  });
}

function handleCreateOAuthUrl(payload: CreateOAuthUrlPayload) {
  return auth.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
        'Content-Type': 'application/json',
      },
    });
    return authorizedAxiosInstance.post(API_ENDPOINT, payload);
  });
}

function* watchFetchOAuthUrls() {
  try {
    yield put(setOAuthUrlsLoading({ state: LoadingStates.LOADING }));

    const result = yield call(handleFetchOAuthUrls);
    const oauthUrls: OAuthUrl[] = result.data?.data || [];
    yield put(setOAuthUrls(oauthUrls));

    // For testing: Just set loading to done, use existing store data
    yield put(setOAuthUrlsLoading({ state: LoadingStates.DONE }));
  } catch (e: any) {
    console.error('Error fetching OAuth URLs:', e);
    yield put(setOAuthUrlsLoading({ state: LoadingStates.ERROR }));
    yield put(
      addNotification({
        state: LoadingStates.ERROR,
        message: 'Failed to fetch OAuth URLs',
      })
    );
  }
}

function* watchCreateOAuthUrl(action: PayloadAction<CreateOAuthUrlPayload>) {
  try {
    const result = yield call(handleCreateOAuthUrl, action.payload);
    const createdOAuthUrl: OAuthUrl = result.data;
    yield put(setCreateOAuthUrlSuccess(createdOAuthUrl));
    yield put(
      addNotification({
        state: LoadingStates.DONE,
        message: 'OAuth URL created successfully!',
      })
    );
  } catch (e: any) {
    console.error('Error creating OAuth URL:', e);
    let errorMessage = 'Failed to create OAuth URL';

    // Check for errors array in response (first priority)
    if (
      e?.response?.data?.errors &&
      Array.isArray(e.response.data.errors) &&
      e.response.data.errors.length > 0
    ) {
      errorMessage = e.response.data.errors[0];
    } else if (e?.response?.data?.message) {
      // Fallback to message field
      errorMessage = e.response.data.message;
    } else if (e?.message) {
      // Fallback to error message
      errorMessage = e.message;
    }

    yield put(setCreateOAuthUrlError(errorMessage));
    yield put(
      addNotification({
        state: LoadingStates.ERROR,
        message: errorMessage,
      })
    );
  }
}

export default function* watchAll() {
  yield takeLatest(fetchOAuthUrls.type, watchFetchOAuthUrls);
  yield takeLatest(createOAuthUrl.type, watchCreateOAuthUrl);
}
